/**
 * @mentions in the Internal composer (#113): the pure text side. The textarea shows "@Name"; on post every picked
 * "@Name" still in the text becomes Desk's own mention markup (`zsu[@user:<zuid>]zsu`, the person's `token` from
 * GET /mentions/people), so Desk notifies them natively and Patchbay can ring their bell. A name the author deleted
 * is not sent.
 */
import { queryOptions } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import { type MentionablePerson, type MentionPeopleOut, mentionPeopleApiV1MentionsPeopleGet } from '../../lib/api/generated'

export interface PickedMention {
  zuid: string
  name: string
  token: string
}

export const mentionPeopleQuery = () =>
  queryOptions({
    queryKey: ['mentions', 'people'] as const,
    queryFn: ({ signal }): Promise<MentionPeopleOut> => unwrap(mentionPeopleApiV1MentionsPeopleGet({ signal })),
    staleTime: 10 * 60_000,
    retry: 1,
  })

/** The "@query" being typed at the caret: "@" at the start or after whitespace, no newline in the query. */
export function activeMention(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret)
  const at = before.lastIndexOf('@')
  if (at < 0) return null
  if (at > 0 && !/\s/.test(before[at - 1] ?? '')) return null
  const query = before.slice(at + 1)
  if (/[\n@]/.test(query) || query.length > 40) return null
  return { start: at, query }
}

export function filterPeople(people: MentionablePerson[], query: string, limit = 8): MentionablePerson[] {
  const q = query.trim().toLowerCase()
  const hits = q
    ? people.filter(
        (p) =>
          p.name
            .toLowerCase()
            .split(/\s+/)
            .some((w) => w.startsWith(q)) ||
          p.name.toLowerCase().startsWith(q) ||
          (p.email ?? '').toLowerCase().startsWith(q),
      )
    : people
  return hits.slice(0, limit)
}

/** Replace "@query" at [start, caret) with "@Name ". */
export function insertMention(text: string, start: number, caret: number, name: string): { text: string; caret: number } {
  const insert = `@${name} `
  const next = text.slice(0, start) + insert + text.slice(caret).replace(/^ /, '')
  return { text: next, caret: start + insert.length }
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Every picked "@Name" still present becomes its Desk token; returns the body and the people actually mentioned. */
export function applyMentions(text: string, picked: PickedMention[]): { body: string; mentioned: PickedMention[] } {
  const unique = [...new Map(picked.map((p) => [p.zuid, p])).values()].sort((a, b) => b.name.length - a.name.length)
  let body = text
  const mentioned: PickedMention[] = []
  for (const p of unique) {
    const re = new RegExp(`(^|[^\\w@])@${escapeRe(p.name)}(?![\\w])`, 'g')
    if (!re.test(body)) continue
    body = body.replace(re, (_m, lead: string) => `${lead}${p.token}`)
    mentioned.push(p)
  }
  return { body, mentioned }
}
